// Layer: presentation. Group KPI view (PRD §11.3, FR-OC-6).
import { useCallback, useEffect, useState } from "react";
import type { StoriaApi } from "../application/ports";
import type { GroupKpis } from "../domain/types";
import { ErrorPanel, messageOf } from "./ShiftView";

interface Props {
  api: StoriaApi;
  propertyIds: string[];
  windowDays?: number;
}

/**
 * Join the ids into a stable key.
 *
 * A caller passing a fresh array literal each render would otherwise refetch on every
 * render. Keying on the content and rebuilding the array inside keeps the effect's
 * dependencies honest: no suppressed exhaustive-deps warning hiding a real bug.
 */
function keyOf(propertyIds: string[]): string {
  return propertyIds.join(",");
}

interface Metric {
  label: string;
  value: string;
  hint: string;
}

export function KpiView({ api, propertyIds, windowDays = 30 }: Props) {
  const [kpis, setKpis] = useState<GroupKpis | null>(null);
  const [error, setError] = useState<string | null>(null);

  const key = keyOf(propertyIds);
  const load = useCallback(() => {
    setError(null);
    const ids = key.split(",").filter(Boolean);
    if (ids.length === 0) {
      setError("No properties configured for this console.");
      return;
    }
    api
      .loadGroupKpis(ids, windowDays)
      .then(setKpis)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [api, key, windowDays]);

  useEffect(load, [load]);

  if (error) return <ErrorPanel message={error} onRetry={load} />;
  if (!kpis) return <p className="muted" role="status">Loading KPIs…</p>;

  // One property is the normal case for a pilot and for the demo, and the group wording
  // read as a bug there: "Group performance" over a single hotel, and a review score
  // hinted as a "Mean across properties" when it was that one property's own score.
  // A mean of one number is not wrong, it is just not what the words claim to be.
  const count = kpis.properties.length;
  const many = count !== 1;

  const metrics: Metric[] = [
    {
      label: "Revenue / guest night",
      value: `£${kpis.revenue_per_guest_night.toFixed(2)}`,
      hint: "Weighted by STORIA action volume",
    },
    {
      label: "Repeat stay rate",
      value: percent(kpis.repeat_stay_rate),
      hint: "Share of guests with a prior stay",
    },
    {
      label: "Review score",
      value: `${kpis.avg_review_score.toFixed(2)} / 5`,
      hint: many ? "Mean across properties" : "This property's mean",
    },
    {
      label: "Median recovery",
      value: `${kpis.median_recovery_minutes.toFixed(0)} min`,
      hint: "Complaint to resolution",
    },
    {
      label: "Staff adoption",
      value: percent(kpis.staff_dau_ratio),
      hint: "Daily active of rostered staff",
    },
    {
      label: "Actions",
      value: kpis.total_action_count.toLocaleString("en-GB"),
      hint: many ? `Across ${count} properties` : "On this property",
    },
  ];

  return (
    <main aria-label={many ? "Group KPIs" : "Property KPIs"} className="stack">
      <header className="panel-head">
        <h2>{many ? "Group performance" : "Property performance"}</h2>
        <p className="muted">Last {windowDays} days</p>
      </header>
      <dl className="metrics">
        {metrics.map((metric) => (
          <div key={metric.label} className="metric">
            <dt>{metric.label}</dt>
            <dd>{metric.value}</dd>
            <p className="muted">{metric.hint}</p>
          </div>
        ))}
      </dl>
    </main>
  );
}

function percent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`;
}
